define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/_base/window",
	"dojo/dom-class",
	"dojo/dom-construct",
	"dojo/dom-geometry",
	"dojo/dom-style",
	"dojo/query",
	"csi/_base/Deferred",
	"csi/_base/underscore",
	"csi/heading/_base",
	"csi/html",
	"csi/service/imageService",
	"csi/widget/Tooltip",
	"csi/xml"
], function(declare, lang, win, domClass, domConstruct, domGeometry, domStyle, query, Deferred, _, csiHeading, html, {waitForImagesToLoad}, Tooltip, xml) {

	var CSS_CELL = "csiHeadingCell";
	var CSS_CELL_EDITABLE = "csiHeadingCellEditable";
	var CSS_COLUMN = "csiHeadingColumn";
	var CSS_GROUP = "csiHeadingGroup";
	var CSS_LARGE = "csiHeadingCellLarge";
	var CSS_LARGE_SPAN = "csiHeadingCellLargeSpan";
	var CSS_NEXT = "csiHeadingColumnNext";
	var CSS_SPAN = "csiHeadingColumnSpan";
	var CSS_SPAN_TEST = "csiHeadingColumnSpan csiHeadingColumnSpanTest";
	var LAYOUT_GROUP = -1;
	var LAYOUT_SPAN = 0;
	var MIN_CELL_HEIGHT = 30; // this is not an exact number, but should be between the height of one to two lines
	var SINGLE_COLUMN_THRESHOLD = 60; // do not add more columns until at least 2 properties (of normal height)

	function _newTable(/*DOMNode*/domParent) {
		return domConstruct.create("table", {
			"class": "csiHeadingColumnTable",
			border: 0,
			cellpadding: 0,
			cellspacing: 0
		}, domParent);
	}

	/**
	 * Return true if heading is valid to render
	 * @param {Context} ctx 
	 * @param {Spec} spec 
	 * @param {boolean} bValidValues 
	 * @return {boolean}
	 */
	function _isValidSpec(ctx, spec, bValidValues) {
		return csiHeading.applyHeadingRenderer(ctx, spec.heading, (renBase, hBase) => {
			const node = renBase ? ctx.getNode() : null;
			if (!node || !hBase.m_id) {
				// allow non-attribute headings or headings where path does not yet exist
				return true;
			} else if (node[hBase.m_id] === undefined) {
				// filter out optional attributes
				return false;
			} else if (!bValidValues) {
				// render heading since not concerned whether value is populated or not
				return true;
			}

			// filter out attributes with empty values
			const value = renBase._getValue(ctx, hBase);
			return xml.valueIsValid(value, renBase.type);
		});
	}

	return declare(null, {
		parentClass: null,
		bValidValues: false,
		_nColumns: null,
		_nGroups: null,

		constructor: function(/*Object*/params) {
			// FIX: remove if change this to widget
			lang.mixin(this, params);
		},

		/**
		 * Render groups of attributes based on view definition and attribute groups of class
		 * @param {Context} ctx 
		 * @param {HTMLElement} parentElement - parent element where to render content
		 * @param {Specs} specs - specs for attributes to render
		 * @param {number} [nColumns] - number of attribute columns in view
		 * @param {number} [availableWidth] - available width for view
		 * @param {Record<string, number[]>} [layoutByGroup] - allow the layout per group to be specified rather than calculated
		 * @param {Tooltip[]} [tooltips] - if specified, populate tooltips with tooltip for each attribute
		 * @return {Deferred<void>}
		 */
		generate(ctx, parentElement, specs, nColumns, availableWidth, layoutByGroup, tooltips) {
			const dfd = new Deferred();
			const groups = [];
			let elements = [];

			const registerGroup = (name) => {
				if (elements.length) {
					groups.push([name, elements, layoutByGroup?.[name]]);
				}

				elements = [];
			};

			const renderSpec = (spec) => {
				const element = this._generateHeading(ctx, spec, tooltips);
				if (element) {
					elements.push(element);
				}
			};

			html.destroyChildren(parentElement);

			// traverse attribute specs to discover attribute groups
			specs.traverse(false, (spec) => {
				if (spec.isGroup) {
					const {name, subs} = spec;
					registerGroup();
					subs.forEach(renderSpec);
					registerGroup(name);
				} else {
					renderSpec(spec);
				}
			});

			registerGroup();

			if (this._nGroups = groups.length) {
				this._nColumns = nColumns;

				// render groups into table bodies after all images are loaded
				waitForImagesToLoad().then(
					() => {
						if (ctx.view.isValidView()) {
							groups.forEach(([name, elements, layout]) => {
								this._generateGroup(ctx, name, elements, layout, parentElement, availableWidth);
							});
						}

						dfd.resolve();
					},
					(err) => {
						dfd.reject(err);
					}
				);
			} else {
				dfd.resolve();
			}

			return dfd;
		},

		/**
		 * Render group to view - need to add it immediately to view to get correct width/height values
		 * @param {Context} ctx 
		 * @param {string} groupName 
		 * @param {HTMLElement[]} headings 
		 * @param {number[] | undefined} layout
		 * @param {HTMLElement} domRoot 
		 * @param {number | undefined} availableWidth
		 */
		_generateGroup(ctx, groupName, headings, layout, domRoot, availableWidth) {
			var table = _newTable(domRoot);
			var tbodyGroup = domConstruct.create("tbody", null, table);
			var trGroup = domConstruct.create("tr", null, tbodyGroup);
			var tdGroup = domConstruct.create("td", {"class": CSS_COLUMN}, trGroup);
			var tbody = domConstruct.create("tbody", null, _newTable(tdGroup));

			domClass.add(domRoot, CSS_GROUP);

			// add special styling if editing is allowed
			if (!ctx.bPrint && !ctx.isReadOnly()) {
				domClass.add(table, "csiHeadingGroupEdit");
			}

			// add single test heading to calculate column widths based on CSS
			tbody.appendChild(this._generateHeading(ctx));

			// if table is currently not displayed, then temporarily move to absolutely positioned element on body for height calculations
			var divBody;

			if (domGeometry.getContentBox(tdGroup).h <= 0) {
				divBody = domConstruct.create("div", {
					"class": CSS_GROUP + " " + (this.parentClass || ""),
					"style": "position: absolute; left: -10000px"
				}, win.body());

				domConstruct.place(table, divBody);
			}

			// determine width of first column and subsequent columns (which have additional padding by default)
			var w0 = domGeometry.getMarginBox(tdGroup).w;
			domClass.add(tdGroup, CSS_NEXT);
			var w1 = domGeometry.getMarginBox(tdGroup).w;
			domClass.remove(tdGroup, CSS_NEXT);

			// add headings to first column - they have to be added to full DOM in order to get their correct height/width in context
			tbody.removeChild(tbody.firstChild);
			_.each(headings, function(dom) {tbody.appendChild(dom);});

			// determine number of columns
			if ((this._nGroups == 1) && (domGeometry.getContentBox(tdGroup).h < SINGLE_COLUMN_THRESHOLD)) {
				// if only one group, then only introduce more columns if height exceeds some minimum threshold
				this._nColumns = 1;
			} else if (!this._nColumns) {
				// calculate number of columns based on available width
				availableWidth = availableWidth || domGeometry.getContentBox(domRoot).w;
				this._nColumns = 1 + Math.floor(Math.max(availableWidth - w0, 0) / w1);
			} else if (availableWidth) {
				// limit number of columns by given width
				var nMaxColumns = 1 + Math.floor(Math.max(availableWidth - w0, 0) / w1);
				this._nColumns = Math.min(this._nColumns, nMaxColumns);
			}

			// add header if group with name
			if (groupName) {
				const tr = html.create("tr", null, tbodyGroup, "first");
				const td = html.create("td", {colSpan: this._nColumns}, tr);
				html.create("div", {"class": html.CSS_VIEW_TITLE, innerText: groupName}, td);
			}

			// calculate heights of headings with label and value side by side - if a heading is always wide, then force
			// columns to span whole width
			var n = headings.length;

			if (!layout) {
				layout = [];

				var cellHeights = _.map(headings, function(dom) {return domGeometry.getMarginBox(dom).h;});
				var largeCells = query("." + CSS_LARGE, tbody);
				var nLarge = largeCells.length;
				var bLargeSpan = !!query("." + CSS_LARGE_SPAN, tbody).length;

				if (!bLargeSpan) {
					// if there are cells that could be rendered as column spans, and there is overflow in any of them,
					// then force to render as column spans
					bLargeSpan = _.some(largeCells, function(tr) {
						var tdValue = tr.lastChild;
						return tdValue.scrollWidth > tdValue.offsetWidth;
					});
				}

				var height = bLargeSpan ? 100000 : this._getGroupHeight(layout, cellHeights);

				// if there are large headings, then also calculate heights with value spanning entire table width
				if (nLarge) {
					// consider two more possible layouts - one where all large headings span all columns,
					// and another where a threshold (50) is used to determine if large headings should span all columns
					var spanHeights = new Array(n);
					var spanHeightsWithThreshold = new Array(n);
					var spanIndices = [];

					this._spanWidth = w0 + w1 * (this._nColumns - 1);

					// determine heights of values when spanning all columns
					tdGroup.colSpan = this._nColumns;
					domClass.add(tdGroup, CSS_SPAN_TEST);
					domStyle.set(tdGroup, "width", this._spanWidth + "px");

					_.each(headings, function(domNode, i) {
						if (domClass.contains(domNode, CSS_LARGE_SPAN) || (domClass.contains(domNode, CSS_LARGE) && domNode.offsetHeight > MIN_CELL_HEIGHT)) {
							spanHeights[i] = domGeometry.getMarginBox(domNode).h;
							spanIndices.push(i);
						} else {
							spanHeights[i] = spanHeightsWithThreshold[i] = 0;
						}
					}, this);

					domClass.remove(tdGroup, CSS_SPAN_TEST);
					domStyle.set(tdGroup, "width", "");
					tdGroup.colSpan = 1;

					// add height of header that will appear above spanned value
					_.each(spanIndices, function(si, i) {
						var domNode = headings[si];
						var force = domClass.contains(domNode, CSS_LARGE_SPAN);
						var h = domGeometry.getMarginBox(domNode.firstChild.firstChild).h;
						var hSpan = spanHeights[si] + h;
						var diff = cellHeights[si] - hSpan;

						if (!force) {
							// if surrounded by other spans, then force this to span as well
							var s0 = spanIndices[i - 1];
							if (s0 == null) s0 = si - 1;

							var s2 = spanIndices[i + 1];
							if (s2 == null) s2 = si + 1;

							force = (s0 == si - 1) && (s2 == si + 1);
						}

						spanHeights[si] = hSpan;
						spanHeightsWithThreshold[si] = (force || (diff > 25)) ? hSpan : 0;
					}, this);

					// determine which configuration of headings results in least amount of height
					var spanLayout = [];
					var spanHeight = this._getGroupHeight(spanLayout, cellHeights, spanHeights);

					if (spanHeight < height) {
						layout = spanLayout;
						height = spanHeight;
					}

					spanLayout = [];
					spanHeight = this._getGroupHeight(spanLayout, cellHeights, spanHeightsWithThreshold);

					if (spanHeight < height) {
						layout = spanLayout;
					}
				}
			}

			// generate layout, but before doing so, remove row containing headings - just makes layout algorithm easier
			// to not start with some of the headings already in the DOM
			tbodyGroup.removeChild(trGroup);

			if (divBody) {
				domConstruct.place(table, domRoot);
				domConstruct.destroy(divBody);
			}

			this._generateLayout(ctx, layout, headings, tbodyGroup);

			// post-processing - look for any consecutive images and place on same row if there is room
			this._postProcessImageSpans(table, availableWidth || this._spanWidth);
		},

		/**
		 * Render label and value for given spec - return null if heading is hidden from view;
		 * the spec can be empty to output test row for size calculations
		 * @param {Context} ctx 
		 * @param {Spec} [spec] 
		 * @param {Tooltip[]} [tooltips]
		 * @return {HTMLElement | null}
		 */
		_generateHeading(ctx, spec, tooltips) {
			if (spec && !_isValidSpec(ctx, spec, this.bValidValues)) {
				return null;
			}

			const h = spec?.heading;
			const td = domConstruct.create("td");
			let nLarge = 0;

			if (h) {
				csiHeading.applyHeadingRenderer(ctx, h, function(renBase, hBase) {
					csiHeading.toHtmlInContext(ctx, td, renBase, hBase);

					if (renBase) {
						nLarge = renBase.canBeLarge(ctx, hBase);
					}
				});

				if (domClass.contains(td, html.CSS_HIDE)) {
					return null;
				}
			}

			const tr = domConstruct.create("tr", {"class": CSS_CELL + " " + (domClass.contains(td, csiHeading.CSS_EDITABLE) ? CSS_CELL_EDITABLE : "")});
			const th = domConstruct.create("th", null, tr);

			html.create("div", {
				"class": "csiHeadingCellLabel",
				multiLineText: h ? this._getHeadingLabel(h) : "Test"
			}, th);

			const headingName = h?.m_name ?? "";
			const headingTooltip = h?.getTooltip() ?? "";

			if (!ctx.view.useMuiTooltipStyling) {
				if (headingTooltip) {
					th.setAttribute("title", headingTooltip);
				}
			} else if (headingName || headingTooltip) {
				tooltips?.push(
					new Tooltip({
						connectId: th,
						label: Tooltip.formatCustomField(headingName, headingTooltip),
						position: ["before-centered"],
						showDelay: Tooltip.TOOLTIP_REACT_SHOW_DELAY,
						onShow: () => {
							Tooltip.applyMuiStyling(true);
						},
					})
				);
			}

			if (nLarge > 0) {
				// mark cell with class(es) as candidate for splitting for better rendering
				domClass.add(tr, CSS_LARGE);

				if (nLarge == 2) {
					domClass.add(tr, CSS_LARGE_SPAN);
				}
			}

			domConstruct.place(td, tr);
			return tr;
		},

		_generateLayout: function(/*Context*/ctx, /*Number[]*/layout, /*DOMNode[]*/headings, /*DOMNode*/tbodyGroup) {
			// summary: input is tbody with all headings in single td
			var n = layout.length;
			var i = -1;
			var c = LAYOUT_GROUP;
			var groupOffset = 0;
			var bOdd;
			var bSpan;
			var cLast;
			var tbody;
			var tr;
			var trGroup;
			var tdGroup;

			var layoutRow = function() {
				// create new tbody for each row so long as it is not the first - we want the group header
				// to always be paired with at least one group of headings
				tbodyGroup = i ? domConstruct.create("tbody", null, tbodyGroup, "after") : tbodyGroup;
				trGroup = domConstruct.create("tr", null, tbodyGroup);
			};

			var layoutColumn = function(columnParams) {
				// div is needed to support inline editing
				tdGroup = domConstruct.create("td", columnParams, trGroup);
				domClass.add(tdGroup, CSS_COLUMN);
				var div = domConstruct.create("div", {style: "position:relative"}, tdGroup);
				tbody = domConstruct.create("tbody", null, _newTable(div));
				bOdd = true;
			};

			while (++i < n) {
				cLast = c;
				c = layout[i];

				if (c == LAYOUT_GROUP) {
					trGroup = null;
					++groupOffset;
					continue;
				} else if (c == LAYOUT_SPAN) {
					// create row for single heading to span all columns
					layoutRow();
					layoutColumn({
						"class": CSS_SPAN,
						colSpan: this._nColumns,
						style: "width:" + this._spanWidth + "px"
					});

					bSpan = true;
				} else if (!trGroup) {
					// create new row
					layoutRow();
					layoutColumn();
				} else if (!trGroup.childNodes[c - 1]) {
					// create new column
					layoutColumn({"class": CSS_NEXT});
				} else {
					// remain in same column
					bOdd = !bOdd;
				}

				tr = headings[i - groupOffset];

				if (tr) {
					tbody.appendChild(tr);

					if (bOdd) {
						domClass.add(tr, "odd");
					}

					// finish styling of span rows by splitting into two
					if (c == LAYOUT_SPAN) {
						domConstruct.create("tr", {"class": CSS_CELL}, tr, "after").appendChild(tr.lastChild);
					}
				}
			}

			// make up for missing columns if layout does not span all columns so that title spans all columns
			if (!bSpan && trGroup) {
				const existingColumns = trGroup.childNodes.length;
				const targetColumns = this._nColumns;

				for (var i = existingColumns; i < targetColumns; ++i) {
					layoutColumn({"class": CSS_NEXT, "style": "visibility:hidden"});
					tr = this._generateHeading(ctx);
					tbody.appendChild(tr);
				}
			}
		},

		_getSubGroupHeight: function(/*Number[]*/layout, /*Number[]*/heights, /*Number*/nStart, /*Number*/nEnd, /*Number*/hTotal, /*Number?*/hTarget) {
			// summary: separate indices from start to end index into columns based on target height
			var nLayoutLength = layout.length;
			var hSubGroup = 0;
			var i = nStart - 1;

			// default for target is evenly divisible by number of columns
			hTarget = hTarget || Math.ceil(hTotal / this._nColumns);

			// add group separator before adding new layout
			if (nLayoutLength) {
				layout.push(LAYOUT_GROUP);
			}

			// traverse those that fit into first column
			while ((++i <= nEnd) && (hSubGroup < hTarget)) {
				layout.push(1);
				hSubGroup += heights[i];
			}

			// target now becomes height of first column plus a little extra to account for slight height
			// differences between renderers; also track max height for return value
			hTarget = hSubGroup + 5;

			// nothing more to do if only one column
			if (this._nColumns == 1) {
				return hSubGroup;
			}

			// assign subsequent items to other columns
			var nColumnStart = i--;
			var h = 0;
			var c = 2;

			while (++i <= nEnd) {
				if ((h + heights[i] > hTarget) && (i > nColumnStart)) {
					// track largest column height
					if (h > hSubGroup) hSubGroup = h;

					// if exceeded column height and at least one in column, then move to next column
					nColumnStart = i;
					++c;

					// reset height for next column
					h = 0;
				}

				layout.push(c);
				h += heights[i];
			}

			// if we have exceeded the number of columns, then try again with larger target; increase target by
			// maximum of 10 or the left-over height spread across all columns. This problem can occur when you have
			// a tall heading that takes up most of one column
			if (c > this._nColumns) {
				// restore original layout before retrying
				layout.splice(nLayoutLength, layout.length - nLayoutLength);

				return this._getSubGroupHeight(layout, heights, nStart, nEnd, hTotal, hTarget - 5 + Math.max(h / this._nColumns, 10));
			}

			// return max height of all columns
			return Math.max(hSubGroup, h);
		},

		_getGroupHeight: function(/*Number[]*/layout, /*Number[]*/h1, /*Number[]?*/h2, /*Number?*/nStart) {
			// summary: sum height of all nodes, optionally stopping after reaching target
			nStart = nStart || 0;

			var n = h1.length;
			var hTotal = 0;
			var hGroup = 0;
			var i = nStart - 1;

			while (++i < n) {
				if (h2 && h2[i]) {
					// reached span, so mark as span column (0), add span height, and split previous into columns
					if (hGroup) {
						hTotal += this._getSubGroupHeight(layout, h1, nStart, i - 1, hGroup);
						hGroup = 0;
						layout.push(LAYOUT_GROUP);
					} else if (nStart) {
						layout.push(LAYOUT_GROUP);
					}

					hTotal += h2[i];
					layout.push(LAYOUT_SPAN);
					nStart = i + 1;
				} else {
					hGroup += h1[i];
				}
			}

			if (hGroup) {
				hTotal += this._getSubGroupHeight(layout, h1, nStart, i - 1, hGroup);

				// make sure all columns are filled if you have enough attributes; example where this is needed is when
				// you have 4 columns and 6 attributes - in that case you get 3 columns of 2 attributes rather then 4 columns
				if (this._nColumns > 2) {
					var fillColumns = function(c, x, y) {
						while ((y - x >= c) && (layout[y - 1] < c)) {
							layout[--y] = c--;
						}
					};

					nStart = 0;

					_.each(layout, function(c, i) {
						if (c == LAYOUT_GROUP) {
							fillColumns(this._nColumns, nStart, i);
							nStart = i + 1;
						}
					}, this);

					fillColumns(this._nColumns, nStart, layout.length);
				}
			}

			// add 10 (must match .csiHeadingColumn padding) for each column group to get extra padding in layout
			_.each(layout, function(c, i) {
				if (!i || (c == LAYOUT_GROUP)) {
					hTotal += 10;
				}
			});

			return hTotal;
		},

		_getHeadingLabel: function(/*Heading*/h) {
			// summary: default is to simply return heading label
			return h.getLabel();
		},

		_postProcessImageSpans: function(/*DOMNode*/table, /*Number*/availableWidth) {
			// summary: look for images spanning entire rows and combine them into the same row if consecutive and there is space available
			var nlImages = query("." + CSS_SPAN + " td.attrImage", table);
			if (!nlImages.length) return;

			// assumes structure of tbody/tr/td.csiHeadingColumnSpan/div/table/tbody/tr/td.attrImage
			var _getColumnSpan = function(td) {return td.parentNode.parentNode.parentNode.parentNode.parentNode;};
			var _getColumnSpanBody = function(tdSpan) {return tdSpan.parentNode.parentNode;};
			var cspan, td0, td1, tbody0, tbody1, trHeader, trImage, w;
			var i = 0;

			while (td1 = nlImages[i++]) {
				cspan = _getColumnSpan(td1);
				tbody1 = _getColumnSpanBody(cspan);
				domClass.add(tbody1, "csiHeadingImageSpan");
				domStyle.set(cspan, "width", "auto");

				// determine if images have consecutive sibling tbody ancestors
				if (tbody0 && (tbody1.previousSibling == tbody0)) {
					// determine if images will fit on same row
					w = domGeometry.getContentBox(td0.parentNode).w + 5 + domGeometry.getContentBox(td1).w;

					if (w < availableWidth) {
						// combine image spans
						trImage = td0.parentNode;
						trHeader = trImage.previousSibling;

						trHeader.appendChild(domConstruct.create("th", {"class": "csiHeadingCellSeparator"}));
						trHeader.appendChild(td1.parentNode.previousSibling.firstChild);

						trImage.appendChild(domConstruct.create("td", {"class": "csiHeadingCellSeparator"}));
						trImage.appendChild(td1);
						table.removeChild(tbody1);

						td1 = td0;
						tbody1 = tbody0;
					}
				}

				td0 = td1;
				tbody0 = tbody1;
			}
		}
	});
});